<template>
  <div class="indiefy-cover-editor-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <div class="hero-text">
            <h1 class="hero-title">
              {{ $t('coverEditor.heroTitle') }}
            </h1>
            <p class="hero-subtitle">
              {{ $t('coverEditor.heroSubtitle') }}
            </p>
            <div class="hero-features">
              <div class="feature-item">
                <i class="fas fa-palette" />
                <span>{{ $t('coverEditor.feature1') }}</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-magic" />
                <span>{{ $t('coverEditor.feature2') }}</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-download" />
                <span>{{ $t('coverEditor.feature3') }}</span>
              </div>
            </div>
            <div class="hero-actions">
              <button
                class="btn btn-primary btn-large"
                @click="redirectToSignup"
              >
                <i class="fas fa-user-plus" />
                {{ $t('coverEditor.getStarted') }}
              </button>
              <button
                class="btn btn-outline btn-large"
                @click="scrollToEditor"
              >
                <i class="fas fa-eye" />
                {{ $t('coverEditor.tryNow') }}
              </button>
            </div>
          </div>
          <div class="hero-media">
            <div class="hero-preview">
              <div class="preview-device">
                <div class="device-screen">
                  <div class="sample-cover">
                    <div class="cover-artwork">
                      <h3>Sample Track</h3>
                      <p>Artist Name</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Cover Editor Demo Section -->
    <section
      ref="editorSection"
      class="editor-section"
    >
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">
            {{ $t('coverEditor.demoTitle') }}
          </h2>
          <p class="section-subtitle">
            {{ $t('coverEditor.demoSubtitle') }}
          </p>
        </div>

        <div class="editor-demo">
          <div class="editor-wrapper">
            <CoverTemplateEditor
              :track-data="dummyTrackData"
              :on-download="handleDownload"
              :is-demo="true"
            />
          </div>
          <div class="demo-notice">
            <div class="notice-content">
              <i class="fas fa-info-circle" />
              <div class="notice-text">
                <h4>{{ $t('coverEditor.demoNoticeTitle') }}</h4>
                <p>{{ $t('coverEditor.demoNoticeText') }}</p>
              </div>
            </div>
            <button
              class="btn btn-primary"
              @click="redirectToSignup"
            >
              <i class="fas fa-rocket" />
              {{ $t('coverEditor.signUpNow') }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">
            {{ $t('coverEditor.featuresTitle') }}
          </h2>
          <p class="section-subtitle">
            {{ $t('coverEditor.featuresSubtitle') }}
          </p>
        </div>

        <div class="features-grid">
          <div
            v-for="(feature, index) in features"
            :key="index"
            class="feature-card"
          >
            <div class="feature-icon">
              <i :class="feature.icon" />
            </div>
            <h3 class="feature-title">
              {{ $t(feature.title) }}
            </h3>
            <p class="feature-description">
              {{ $t(feature.description) }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Templates Preview Section -->
    <section class="templates-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">
            {{ $t('coverEditor.templatesTitle') }}
          </h2>
          <p class="section-subtitle">
            {{ $t('coverEditor.templatesSubtitle') }}
          </p>
        </div>

        <div class="templates-grid">
          <div
            v-for="(template, index) in templateTypes"
            :key="index"
            class="template-card"
          >
            <div
              class="template-preview"
              @contextmenu="preventRightClick"
            >
              <div
                :class="`template-${template.type}`"
                @contextmenu="preventRightClick"
              >
                <h4>{{ template.sampleTitle }}</h4>
                <p>{{ template.sampleArtist }}</p>
              </div>
            </div>
            <h3 class="template-name">
              {{ $t(template.name) }}
            </h3>
            <p class="template-description">
              {{ $t(template.description) }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">
            {{ $t('coverEditor.ctaTitle') }}
          </h2>
          <p class="cta-subtitle">
            {{ $t('coverEditor.ctaSubtitle') }}
          </p>
          <div class="cta-actions">
            <button
              class="btn btn-primary btn-xl"
              @click="redirectToSignup"
            >
              <i class="fas fa-user-plus" />
              {{ $t('coverEditor.createAccount') }}
            </button>
            <button
              class="btn btn-outline btn-xl"
              @click="scrollToEditor"
            >
              <i class="fas fa-paint-brush" />
              {{ $t('coverEditor.tryEditor') }}
            </button>
          </div>
          <div class="cta-note">
            <i class="fas fa-gift" />
            {{ $t('coverEditor.freeToUse') }}
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import CoverTemplateEditor from '@/components/releases/steps/track-components/CoverTemplateEditor.vue';
import { usePageSeo } from '@/composables/usePageSeo';

export default {
  name: 'IndiefyCoverEditorView',
  components: {
    CoverTemplateEditor,
  },
  setup() {
    const router = useRouter();
    const editorSection = ref(null);

    usePageSeo({
      titleKey: 'coverEditor.seoTitle',
      descriptionKey: 'coverEditor.seoDescription',
    });

    // Dummy data for the cover editor
    const dummyTrackData = ref({
      title: 'Sample Track Title',
      artist: 'Demo Artist',
      genre: 'Electronic',
      mood: 'Energetic',
    });

    // Features data
    const features = ref([
      {
        icon: 'fas fa-palette',
        title: 'coverEditor.features.customizable.title',
        description: 'coverEditor.features.customizable.description',
      },
      {
        icon: 'fas fa-bolt',
        title: 'coverEditor.features.instant.title',
        description: 'coverEditor.features.instant.description',
      },
      {
        icon: 'fas fa-mobile-alt',
        title: 'coverEditor.features.responsive.title',
        description: 'coverEditor.features.responsive.description',
      },
      {
        icon: 'fas fa-image',
        title: 'coverEditor.features.highQuality.title',
        description: 'coverEditor.features.highQuality.description',
      },
      {
        icon: 'fas fa-magic',
        title: 'coverEditor.features.templates.title',
        description: 'coverEditor.features.templates.description',
      },
      {
        icon: 'fas fa-download',
        title: 'coverEditor.features.export.title',
        description: 'coverEditor.features.export.description',
      },
    ]);

    // Template types
    const templateTypes = ref([
      {
        type: 'minimal',
        name: 'coverEditor.templates.minimal.name',
        description: 'coverEditor.templates.minimal.description',
        sampleTitle: 'Midnight Waves',
        sampleArtist: 'Ocean Dreams',
      },
      {
        type: 'gradient',
        name: 'coverEditor.templates.gradient.name',
        description: 'coverEditor.templates.gradient.description',
        sampleTitle: 'Neon Nights',
        sampleArtist: 'Synth Wave',
      },
      {
        type: 'vintage',
        name: 'coverEditor.templates.vintage.name',
        description: 'coverEditor.templates.vintage.description',
        sampleTitle: 'Golden Era',
        sampleArtist: 'Retro Soul',
      },
      {
        type: 'abstract',
        name: 'coverEditor.templates.abstract.name',
        description: 'coverEditor.templates.abstract.description',
        sampleTitle: 'Cosmic Flow',
        sampleArtist: 'Space Echo',
      },
      {
        type: 'artistic',
        name: 'coverEditor.templates.artistic.name',
        description: 'coverEditor.templates.artistic.description',
        sampleTitle: 'Canvas Dreams',
        sampleArtist: 'Art Collective',
      },
    ]);

    // Function to redirect to signup
    const redirectToSignup = () => {
      router.push({ name: 'signup' });
    };

    // Function to scroll to editor
    const scrollToEditor = () => {
      if (editorSection.value) {
        editorSection.value.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    };

    // Handle download button click (redirect to signup)
    const handleDownload = () => {
      redirectToSignup();
    };

    // Prevent right-click on template previews
    const preventRightClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      return false;
    };

    return {
      dummyTrackData,
      features,
      templateTypes,
      editorSection,
      redirectToSignup,
      scrollToEditor,
      handleDownload,
      preventRightClick,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/breakpoints";

.indiefy-cover-editor-page {
  min-height: 100vh;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }
}

// Hero Section
.hero-section {
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;

  @media (max-width: $mobile) {
    min-height: auto;
    padding: 4rem 0;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%);
    z-index: 1;
  }

  .container {
    position: relative;
    z-index: 2;
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }

  .hero-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;

    @media (max-width: $tablet) {
      grid-template-columns: 1fr;
      gap: 3rem;
      text-align: center;
    }

    @media (max-width: $mobile) {
      gap: 2rem;
      padding: 1rem 0;
    }
  }

  .hero-text {
    color: white;

    .hero-title {
      font-size: 3.5rem;
      font-weight: 800;
      line-height: 1.1;
      margin-bottom: 1.5rem;

      @media (max-width: $tablet) {
        font-size: 2.5rem;
        margin-bottom: 1rem;
      }

      @media (max-width: $mobile) {
        font-size: 1.8rem;
        line-height: 1.2;
        margin-bottom: 0.75rem;
      }
    }

    .hero-subtitle {
      font-size: 1.25rem;
      line-height: 1.6;
      margin-bottom: 2rem;
      opacity: 0.9;

      @media (max-width: $tablet) {
        margin-bottom: 1.5rem;
      }

      @media (max-width: $mobile) {
        font-size: 1rem;
        line-height: 1.5;
        margin-bottom: 1rem;
      }
    }

    .hero-features {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-bottom: 2.5rem;

      @media (max-width: $tablet) {
        margin-bottom: 2rem;
        gap: 0.75rem;
      }

      @media (max-width: $mobile) {
        margin-bottom: 1.5rem;
        gap: 0.5rem;
      }

      .feature-item {
        display: flex;
        align-items: center;
        gap: 1rem;

        @media (max-width: $mobile) {
          gap: 0.75rem;
          justify-content: center;
        }

        i {
          font-size: 1.2rem;
          color: #ffd700;
          width: 20px;

          @media (max-width: $mobile) {
            font-size: 1rem;
            width: 16px;
          }
        }

        span {
          font-size: 1rem;
          opacity: 0.9;

          @media (max-width: $mobile) {
            font-size: 0.9rem;
          }
        }
      }
    }

    .hero-actions {
      display: flex;
      gap: 1rem;

      @media (max-width: $tablet) {
        gap: 0.75rem;
      }

      @media (max-width: $mobile) {
        flex-direction: column;
        gap: 0.75rem;
        width: 100%;
      }
    }
  }

  .hero-media {
    .hero-preview {
      display: flex;
      justify-content: center;
      align-items: center;

      .preview-device {
        background: rgba(255, 255, 255, 0.1);
        padding: 2rem;
        border-radius: 20px;
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2);

        @media (max-width: $tablet) {
          padding: 1.5rem;
          border-radius: 15px;
        }

        @media (max-width: $mobile) {
          padding: 1rem;
          border-radius: 12px;
          max-width: 100%;
        }

        .device-screen {
          background: white;
          border-radius: 15px;
          padding: 1.5rem;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);

          @media (max-width: $tablet) {
            padding: 1.25rem;
            border-radius: 12px;
            box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
          }

          @media (max-width: $mobile) {
            padding: 1rem;
            border-radius: 10px;
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
          }

          .sample-cover {
            width: 300px;
            height: 300px;
            background: linear-gradient(45deg, #667eea, #764ba2);
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;

            @media (max-width: $tablet) {
              width: 250px;
              height: 250px;
            }

            @media (max-width: $mobile) {
              width: 200px;
              height: 200px;
              border-radius: 8px;
            }

            .cover-artwork {
              text-align: center;
              color: white;

              h3 {
                font-size: 1.5rem;
                font-weight: 700;
                margin-bottom: 0.5rem;

                @media (max-width: $tablet) {
                  font-size: 1.3rem;
                }

                @media (max-width: $mobile) {
                  font-size: 1.1rem;
                  margin-bottom: 0.25rem;
                }
              }

              p {
                font-size: 1rem;
                opacity: 0.9;

                @media (max-width: $tablet) {
                  font-size: 0.9rem;
                }

                @media (max-width: $mobile) {
                  font-size: 0.8rem;
                }
              }
            }
          }
        }
      }
    }
  }
}

// Editor Section
.editor-section {
  padding: 8rem 0;
  background: #f8fafc;

  .section-header {
    text-align: center;
    margin-bottom: 4rem;

    .section-title {
      font-size: 2.5rem;
      font-weight: 700;
      color: #2d3748;
      margin-bottom: 1rem;
    }

    .section-subtitle {
      font-size: 1.2rem;
      color: #718096;
      max-width: 600px;
      margin: 0 auto;
    }
  }

  .editor-demo {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 3rem;
    align-items: start;

    @media (max-width: $tablet) {
      grid-template-columns: 1fr;
      gap: 2rem;
    }

    .editor-wrapper {
      background: white;
      border-radius: 20px;
      padding: 2rem;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    }

    .demo-notice {
      background: white;
      border-radius: 15px;
      padding: 2rem;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
      border-left: 4px solid #667eea;

      .notice-content {
        display: flex;
        gap: 1rem;
        margin-bottom: 1.5rem;

        i {
          font-size: 1.5rem;
          color: #667eea;
          margin-top: 0.2rem;
          flex-shrink: 0;
        }

        .notice-text {
          h4 {
            font-size: 1.2rem;
            font-weight: 600;
            color: #2d3748;
            margin-bottom: 0.5rem;
          }

          p {
            color: #718096;
            line-height: 1.6;
          }
        }
      }
    }
  }
}

// Features Section
.features-section {
  padding: 8rem 0;
  background: white;

  @media (max-width: $tablet) {
    padding: 6rem 0;
  }

  @media (max-width: $mobile) {
    padding: 4rem 0;
  }

  .container {
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }

  .section-header {
    text-align: center;
    margin-bottom: 6rem;

    @media (max-width: $tablet) {
      margin-bottom: 4rem;
    }

    @media (max-width: $mobile) {
      margin-bottom: 3rem;
    }

    .section-title {
      font-size: 2.5rem;
      font-weight: 700;
      color: #2d3748;
      margin-bottom: 1rem;

      @media (max-width: $tablet) {
        font-size: 2rem;
      }

      @media (max-width: $mobile) {
        font-size: 1.6rem;
        margin-bottom: 0.75rem;
      }
    }

    .section-subtitle {
      font-size: 1.2rem;
      color: #718096;
      max-width: 600px;
      margin: 0 auto;

      @media (max-width: $mobile) {
        font-size: 1rem;
        max-width: 100%;
        padding: 0 1rem;
      }
    }
  }

  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 3rem;

    @media (max-width: $tablet) {
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 2rem;
    }

    @media (max-width: $mobile) {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }

    .feature-card {
      text-align: center;
      padding: 2rem;
      background: #f8fafc;
      border-radius: 15px;
      transition: transform 0.3s ease, box-shadow 0.3s ease;

      @media (max-width: $mobile) {
        padding: 1.5rem;
        border-radius: 12px;
      }

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);

        @media (max-width: $mobile) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
        }
      }

      .feature-icon {
        width: 80px;
        height: 80px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1.5rem;

        @media (max-width: $mobile) {
          width: 60px;
          height: 60px;
          margin-bottom: 1rem;
        }

        i {
          font-size: 2rem;
          color: white;

          @media (max-width: $mobile) {
            font-size: 1.5rem;
          }
        }
      }

      .feature-title {
        font-size: 1.3rem;
        font-weight: 600;
        color: #2d3748;
        margin-bottom: 1rem;

        @media (max-width: $mobile) {
          font-size: 1.1rem;
          margin-bottom: 0.75rem;
        }
      }

      .feature-description {
        color: #718096;
        line-height: 1.6;

        @media (max-width: $mobile) {
          font-size: 0.9rem;
          line-height: 1.5;
        }
      }
    }
  }
}

// Templates Section
.templates-section {
  padding: 8rem 0;
  background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%);

  @media (max-width: $tablet) {
    padding: 6rem 0;
  }

  @media (max-width: $mobile) {
    padding: 4rem 0;
  }

  .container {
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }

  .section-header {
    text-align: center;
    margin-bottom: 6rem;

    @media (max-width: $tablet) {
      margin-bottom: 4rem;
    }

    @media (max-width: $mobile) {
      margin-bottom: 3rem;
    }

    .section-title {
      font-size: 2.5rem;
      font-weight: 700;
      color: #2d3748;
      margin-bottom: 1rem;

      @media (max-width: $tablet) {
        font-size: 2rem;
      }

      @media (max-width: $mobile) {
        font-size: 1.6rem;
        margin-bottom: 0.75rem;
      }
    }

    .section-subtitle {
      font-size: 1.2rem;
      color: #718096;
      max-width: 600px;
      margin: 0 auto;

      @media (max-width: $mobile) {
        font-size: 1rem;
        max-width: 100%;
        padding: 0 1rem;
      }
    }
  }

  .templates-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;

    @media (max-width: $tablet) {
      gap: 1.5rem;
    }

    @media (max-width: $mobile) {
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
    }

    .template-card {
      background: white;
      border-radius: 15px;
      padding: 1.5rem;
      text-align: center;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
      transition: transform 0.3s ease;

      @media (max-width: $mobile) {
        padding: 1rem;
        border-radius: 12px;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
      }

      &:hover {
        transform: translateY(-5px);

        @media (max-width: $mobile) {
          transform: translateY(-2px);
        }
      }

      .template-preview {
        margin-bottom: 1.5rem;
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        pointer-events: auto;

        div[class*="template-"] {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          width: 150px;
          height: 150px;
          margin: 0 auto;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: white;
          text-align: center;

          h4 {
            font-size: 1rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
          }

          p {
            font-size: 0.8rem;
            opacity: 0.9;
          }

          &.template-minimal {
            background: linear-gradient(45deg, #667eea, #764ba2);
          }

          &.template-gradient {
            background: linear-gradient(45deg, #ff6b6b, #ffd93d);
          }

          &.template-vintage {
            background: linear-gradient(45deg, #8b4513, #daa520);
          }

          &.template-abstract {
            background: linear-gradient(45deg, #9d50bb, #6e48aa);
          }

          &.template-artistic {
            background: linear-gradient(45deg, #4ecdc4, #44a08d);
          }
        }
      }

      .template-name {
        font-size: 1.2rem;
        font-weight: 600;
        color: #2d3748;
        margin-bottom: 0.5rem;
      }

      .template-description {
        color: #718096;
        font-size: 0.9rem;
        line-height: 1.5;
      }
    }
  }
}

// CTA Section
.cta-section {
  padding: 8rem 0;
  background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  color: white;
  text-align: center;

  @media (max-width: $tablet) {
    padding: 6rem 0;
  }

  @media (max-width: $mobile) {
    padding: 4rem 0;
  }

  .container {
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }

  .cta-title {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;

    @media (max-width: $tablet) {
      font-size: 2rem;
    }

    @media (max-width: $mobile) {
      font-size: 1.6rem;
      margin-bottom: 0.75rem;
    }
  }

  .cta-subtitle {
    font-size: 1.2rem;
    opacity: 0.9;
    margin-bottom: 3rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;

    @media (max-width: $tablet) {
      margin-bottom: 2.5rem;
    }

    @media (max-width: $mobile) {
      font-size: 1rem;
      margin-bottom: 2rem;
      max-width: 100%;
      padding: 0 1rem;
    }
  }

  .cta-actions {
    display: flex;
    justify-content: center;
    gap: 1.5rem;
    margin-bottom: 2rem;

    @media (max-width: $tablet) {
      gap: 1rem;
    }

    @media (max-width: $mobile) {
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
    }
  }

  .cta-note {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    opacity: 0.8;
    font-size: 0.9rem;

    @media (max-width: $mobile) {
      font-size: 0.85rem;
      gap: 0.4rem;
    }

    i {
      color: #48bb78;
    }
  }
}

// Button Styles
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  justify-content: center;

  @media (max-width: $mobile) {
    width: 100%;
    padding: 0.9rem 1.5rem;
    font-size: 0.95rem;
    gap: 0.4rem;
  }

  &.btn-large {
    padding: 1.2rem 2.5rem;
    font-size: 1.1rem;

    @media (max-width: $mobile) {
      padding: 1rem 2rem;
      font-size: 1rem;
    }
  }

  &.btn-xl {
    padding: 1.5rem 3rem;
    font-size: 1.2rem;

    @media (max-width: $mobile) {
      padding: 1.2rem 2rem;
      font-size: 1.1rem;
    }
  }

  &.btn-primary {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);

      @media (max-width: $mobile) {
        transform: translateY(-1px);
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
      }
    }
  }

  &.btn-outline {
    background: transparent;
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.5);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
}
</style>
